import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { FEE_TYPES } from './schema';

const DOC_ID = 'orderFeeTypes';

/** Key order matches previous hardcoded `FEE_TYPES` object. */
const FEE_TYPE_KEY_ORDER = [
  'ARTWORK',
  'SETUP',
  'EXTRA_SCREEN',
  'RUSH',
  'FLASH',
  'SHIPPING',
  'DOUBLE_SIDED',
  'DESIGN',
  'INK_CHANGE',
  'DELIVERY',
  'DISCOUNT'
];

export const orderFeeTypesDocRef = (db) => doc(db, 'settings', DOC_ID);

export function buildDefaultOrderFeeTypes() {
  return FEE_TYPE_KEY_ORDER.map((key, sortOrder) => ({
    label: FEE_TYPES[key],
    sortOrder,
    taxable: true
  }));
}

/**
 * @param {Array<{ label?: string, sortOrder?: number, taxable?: boolean }>} raw
 * @returns {{ label: string, sortOrder: number, taxable: boolean }[]}
 */
export function normalizeOrderFeeTypes(raw) {
  if (!Array.isArray(raw)) return [];

  let types = raw
    .map((t, i) => ({
      label: String(t?.label ?? '').trim(),
      sortOrder: Number.isFinite(Number(t?.sortOrder)) ? Number(t.sortOrder) : i,
      taxable: t?.taxable === false ? false : true
    }))
    .filter((t) => t.label);

  const seen = new Set();
  types = types.filter((t) => {
    const k = t.label.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  types.sort((a, b) => a.sortOrder - b.sortOrder);
  types.forEach((t, i) => {
    t.sortOrder = i;
  });

  return types;
}

/**
 * Maps lowercase fee type label → taxable (for tagging lines in FeesModal on save).
 */
export function feeTypeTaxableByLabelMap(types) {
  const map = Object.create(null);
  (Array.isArray(types) ? types : []).forEach((t) => {
    const label = String(t?.label ?? '').trim().toLowerCase();
    if (!label) return;
    map[label] = t?.taxable !== false;
  });
  return map;
}

/**
 * @returns {{ types: ReturnType<normalizeOrderFeeTypes>, fromFirestore: boolean }}
 */
export async function getOrderFeeTypes(db) {
  const ref = orderFeeTypesDocRef(db);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    return { types: buildDefaultOrderFeeTypes(), fromFirestore: false };
  }
  const normalized = normalizeOrderFeeTypes(snap.data()?.types);
  return {
    types: normalized.length ? normalized : buildDefaultOrderFeeTypes(),
    fromFirestore: true
  };
}

/**
 * @param {(types: ReturnType<normalizeOrderFeeTypes>) => void} onNext
 * @returns {() => void} unsubscribe
 */
export function subscribeOrderFeeTypes(db, onNext, onError) {
  return onSnapshot(
    orderFeeTypesDocRef(db),
    (snap) => {
      if (!snap.exists()) {
        onNext(buildDefaultOrderFeeTypes());
        return;
      }
      const normalized = normalizeOrderFeeTypes(snap.data()?.types);
      onNext(normalized.length ? normalized : buildDefaultOrderFeeTypes());
    },
    onError
  );
}

/**
 * @param {Array<{ label: string, sortOrder?: number, taxable?: boolean }>} types
 */
export async function saveOrderFeeTypes(db, types) {
  const withOrder = (Array.isArray(types) ? types : []).map((t, i) => ({
    ...t,
    sortOrder: i
  }));
  const normalized = normalizeOrderFeeTypes(withOrder);
  if (!normalized.length) {
    throw new Error('Add at least one fee type with a name.');
  }
  await setDoc(orderFeeTypesDocRef(db), {
    types: normalized,
    updatedAt: serverTimestamp()
  });
}
